import moment from 'moment';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { Option } from '@bringg/react-components/dist/components/filter-bar';
import { FilterBar2ListItem, DateRangeV2, DateRangeKeyword } from '@bringg/react-components';
import { Team as BringgTeam, ServiceArea, Team, TeamServiceArea } from '@bringg/types';

import TeamsStore from 'bringg-web/stores/teams/teams-store';
import { FilterObject } from './filter-bar';
import { DATE_RANGE_FILTERS } from 'bringg-web/features/filter-bar/consts';

export const mapItemsToFilter = (storeItems: Option<number>[]) =>
	storeItems.map(item => ({
		label: item.name,
		value: item.id
	}));

export const mapTeamToFilter = (teams: (BringgTeam & { childrenTeamIds: number[] })[], teamsStore: TeamsStore) =>
	teams.map(item => ({
		label: item.name,
		value: item.id,
		children: item.childrenTeamIds.length
			? mapItemsToFilter(item.childrenTeamIds.map(id => teamsStore.get(id)))
			: []
	}));

const getDateRangeFilters = (filtersList: Partial<FilterObject>) => {
	return DATE_RANGE_FILTERS.reduce((acc, descriptor) => {
		const {
			outputField,
			inputField: { end, start }
		} = descriptor;

		const startFilterValue = filtersList[start] as string | moment.Moment;
		const endFilterValue = filtersList[end] as string | moment.Moment;

		if (startFilterValue && endFilterValue) {
			if (DateRangeV2.isPairOfDateRangeKeywords([startFilterValue, endFilterValue])) {
				return {
					...acc,
					[outputField]: [startFilterValue, startFilterValue]
				};
			}

			return {
				...acc,
				[outputField]: [moment(startFilterValue), moment(endFilterValue)]
			};
		}

		return {
			...acc,
			[outputField]: undefined
		};
	}, {});
};

export const getNormalisedDateRangeFilters = (filters: Record<string, unknown>): Partial<FilterObject> => {
	return DATE_RANGE_FILTERS.reduce((acc, descriptor) => {
		const {
			inputField: { start: startField, end: endField }
		} = descriptor;

		const [startValue, endValue] = [filters[startField], filters[endField]];

		if (DateRangeV2.isPairOfDateRangeKeywords([startValue, endValue])) {
			if (startValue === endValue) {
				const [startDate, endDate] = DateRangeV2.parseKeywordToDateRange(startValue as DateRangeKeyword);
				Object.assign(acc, {
					[startField]: startDate,
					[endField]: endDate
				});
			}
		}
		return acc;
	}, {});
};

export const initialFilterList = (filtersList: Partial<FilterObject>) => {
	return {
		...filtersList,
		dates:
			filtersList.timeWindowStart && filtersList.timeWindowEnd
				? [moment(filtersList.timeWindowStart), moment(filtersList.timeWindowEnd)]
				: undefined,
		sla:
			filtersList.slaStartTime && filtersList.slaEndTime
				? [moment(filtersList.slaStartTime), moment(filtersList.slaEndTime)]
				: undefined,
		reservedUntil:
			filtersList.reservedUntilStartTime && filtersList.reservedUntilEndTime
				? [moment(filtersList.reservedUntilStartTime), moment(filtersList.reservedUntilEndTime)]
				: undefined
	};
};

export const initialFilterListV2 = (
	filtersList: Partial<FilterObject>,
	{ isDateTimeRangeDynamicRangesEnabled }: { isDateTimeRangeDynamicRangesEnabled: boolean }
) => {
	if (isDateTimeRangeDynamicRangesEnabled) {
		return {
			...filtersList,
			...getDateRangeFilters(filtersList)
		};
	}

	return initialFilterList(filtersList);
};

export const buildFilterObject = (updatedFilterObj: Partial<FilterObject>, filterObj: Partial<FilterObject>) => {
	const newFilterObj = Object.assign({}, updatedFilterObj);
	if (Object.prototype.hasOwnProperty.call(updatedFilterObj, 'dates')) {
		delete newFilterObj.dates;
		newFilterObj.timeWindowStart = updatedFilterObj.dates?.length ? updatedFilterObj.dates[0] : undefined;
		newFilterObj.timeWindowEnd = updatedFilterObj.dates?.length > 1 ? updatedFilterObj.dates[1] : undefined;
	}
	if (Object.prototype.hasOwnProperty.call(updatedFilterObj, 'sla')) {
		delete newFilterObj.sla;
		newFilterObj.slaStartTime = updatedFilterObj.sla?.length ? updatedFilterObj.sla[0] : undefined;
		newFilterObj.slaEndTime = updatedFilterObj.sla?.length > 1 ? updatedFilterObj.sla[1] : undefined;
	}
	if (Object.prototype.hasOwnProperty.call(updatedFilterObj, 'reservedUntil')) {
		delete newFilterObj.reservedUntil;
		newFilterObj.reservedUntilStartTime = updatedFilterObj.reservedUntil?.length
			? updatedFilterObj.reservedUntil[0]
			: undefined;
		newFilterObj.reservedUntilEndTime =
			updatedFilterObj.reservedUntil?.length > 1 ? updatedFilterObj.reservedUntil[1] : undefined;
	}
	return { ...filterObj, ...newFilterObj };
};

export const sortAdditionalFilter = (additionals: FilterBar2ListItem[], addedFilters: string[]) => {
	const hiddenAdditionals = additionals.filter(additional => !addedFilters.includes(additional.name));
	const sortedActiveAdditionals = addedFilters.map(filterName =>
		additionals.find(additional => filterName === additional.name)
	);
	return [...hiddenAdditionals, ...sortedActiveAdditionals.filter(x => x)];
};

export const sendFiltersAnalyticsEvent = async (additionalFiltersSnapShot: string[]): Promise<void> => {
	const { sdk } = getRootEnv().dashboardSdk;

	const snapShot = additionalFiltersSnapShot?.length ? additionalFiltersSnapShot.sort().join(':') : '-';

	await sdk.userAnalytics.trackEvent({
		key: 'CA:filter-changed',
		data: {
			filterComb: snapShot
		}
	});
};

export const getServiceAreasOptions = (
	teams: Team[],
	serviceAreasGroupByTeam: Map<number, TeamServiceArea[]>,
	serviceAreas: Map<number, ServiceArea>
) => {
	const today = moment();
	const serviceAreasOptionsMap = teams.reduce((acc, { id: teamId }) => {
		serviceAreasGroupByTeam.get(teamId)?.forEach(teamArea => {
			const serviceArea = serviceAreas.get(teamArea.service_area_id);

			if (!teamArea.effective_end_time || moment(teamArea.effective_end_time).isAfter(today, 'day')) {
				acc.set(serviceArea.id, {
					id: serviceArea.id,
					name: serviceArea.name ?? String(serviceArea.id)
				});
			}
		});
		return acc;
	}, new Map());

	return Array.from(serviceAreasOptionsMap.values());
};
